import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, CalendarDays, HelpCircle, Loader2, Search, ShoppingBasket, X } from "lucide-react";
import api, { normalizeImageUrl } from "@/lib/api";

const Group = ({ icon: Icon, title, children }) => (
  <div className="space-y-1">
    <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#8C8C8C] font-semibold px-2 pt-3"><Icon className="w-3.5 h-3.5" /> {title}</p>
    {children}
  </div>
);

const Row = ({ to, img, title, sub, onClick, testId }) => (
  <Link to={to} onClick={onClick} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-[#f6f3ec] transition-colors" data-testid={testId}>
    {img !== undefined && (
      <span className="w-11 h-11 rounded-lg overflow-hidden bg-[#dbe3c8] flex-shrink-0">{img && <img src={normalizeImageUrl(img)} alt="" className="w-full h-full object-cover" />}</span>
    )}
    <span className="min-w-0">
      <span className="block font-medium text-[#2C2C2C] truncate">{title}</span>
      {sub && <span className="block text-xs text-[#8C8C8C] line-clamp-1">{sub}</span>}
    </span>
  </Link>
);

// Site-wide quick search (experiences, catalogs, shop products, FAQ).
export const SiteSearch = ({ onClose, isIt }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setRes(null);
      setBusy(false);
      return;
    }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q: q.trim() } })
        .then((r) => !cancelled && setRes(r.data))
        .catch(() => !cancelled && setRes(null))
        .finally(() => !cancelled && setBusy(false));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  const empty = res && !res.events.length && !res.catalogs.length && !res.products.length && !res.faqs.length;

  return (
    <div className="fixed inset-0 z-[10003] bg-black/50 backdrop-blur-sm flex items-start justify-center p-0 sm:p-6 sm:pt-24 animate-fade-in" onClick={onClose} data-testid="site-search">
      <div className="bg-white w-full sm:max-w-xl h-full sm:h-auto sm:max-h-[70vh] sm:rounded-3xl shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E0D8]">
          <Search className="w-5 h-5 text-[#8C8C8C]" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder={isIt ? "Cerca esperienze, prodotti, cataloghi..." : "Search experiences, products, catalogs..."} className="flex-1 py-2 text-base bg-transparent border-0 shadow-none outline-none focus:outline-none focus:ring-0 focus-visible:ring-0" data-testid="site-search-input" />
          {busy && <Loader2 className="w-4 h-4 animate-spin text-[#8C8C8C]" />}
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#f0ede6] flex items-center justify-center" aria-label="Chiudi" data-testid="site-search-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto p-2 pb-4" data-testid="site-search-results">
          {!res && <p className="text-sm text-[#8C8C8C] px-2 py-6 text-center">{isIt ? "Scrivi almeno 2 lettere" : "Type at least 2 letters"}</p>}
          {empty && <p className="text-sm text-[#8C8C8C] px-2 py-6 text-center" data-testid="site-search-empty">{isIt ? "Nessun risultato" : "No results"}</p>}
          {res?.events.length > 0 && (
            <Group icon={CalendarDays} title={isIt ? "Esperienze" : "Experiences"}>
              {res.events.map((e) => <Row key={e.id} to={`/esperienze/${e.id}`} img={e.image_url || ""} title={e.title} sub={e.description} onClick={onClose} testId={`search-event-${e.id}`} />)}
            </Group>
          )}
          {res?.catalogs.length > 0 && (
            <Group icon={BookOpen} title={isIt ? "Cataloghi" : "Catalogs"}>
              {res.catalogs.map((c) => <Row key={c.id} to={`/negozio/catalogo/${c.slug}`} title={isIt ? c.title_it : c.title_en || c.title_it} sub={c.description_it} onClick={onClose} testId={`search-catalog-${c.id}`} />)}
            </Group>
          )}
          {res?.products.length > 0 && (
            <Group icon={ShoppingBasket} title={isIt ? "Prodotti del negozio" : "Shop products"}>
              {res.products.map((p, i) => <Row key={i} to="/negozio" img={p.image || ""} title={isIt ? p.name_it : p.name_en || p.name_it} sub={isIt ? "Disponibile in negozio" : "Available in our shop"} onClick={onClose} testId={`search-product-${i}`} />)}
            </Group>
          )}
          {res?.faqs.length > 0 && (
            <Group icon={HelpCircle} title="FAQ">
              {res.faqs.map((f, i) => (
                <div key={i} className="rounded-xl px-2 py-2" data-testid={`search-faq-${i}`}>
                  <p className="font-medium text-[#2C2C2C]">{isIt ? f.question_it : f.question_en || f.question_it}</p>
                  <p className="text-sm text-[#5C5C5C]">{isIt ? f.answer_it : f.answer_en || f.answer_it}</p>
                </div>
              ))}
            </Group>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteSearch;
